import { useT } from "../../../core/i18n";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import {
  getMyDailyAttendanceList,
  teamDailyAttendance,
} from "../../../core/data/redux/reportingEmployeesSlice";
import Toasts from "../../../core/common/toast";
import AttendanceDetailsModel from "../../../core/modals/attendanceDetailsModel";
import { DateFormat } from "../../../core/hooks/dateFormat";
import { Avatar } from "antd";
import { randomAvatar } from "../../../core/hooks/commonFunctions";
import dayjs from "dayjs";
import { formatDateTime } from "../../../utils/dateUtils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AttendanceFilter, { AttendanceFilterValues } from "../../../core/common/AttendanceFilter";
import { formatEmpIdForDisplay, formatEmployeeId } from "../../../utils/empIdUtils";

// Helper function to check if search matches Emp ID (handles original, numeric, and 8-digit zero padded)
const matchesEmpId = (empId: any, searchValue: string): boolean => {
  if (!empId || !searchValue || !searchValue.trim()) return false;

  const empIdStr = empId.toString().toLowerCase().trim();
  const formattedEmpId = formatEmpIdForDisplay(empId).toLowerCase().trim();
  const searchStr = searchValue.toLowerCase().trim();
  const paddedSearchStr = formatEmployeeId(searchValue).toLowerCase().trim();

  // Check original, formatted, and 8-digit padded Emp ID
  return (
    empIdStr.includes(searchStr) ||
    formattedEmpId.includes(searchStr) ||
    empIdStr.includes(paddedSearchStr) ||
    paddedSearchStr.includes(empIdStr)
  );
};

const EmployeesDailyAttendance = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentItem, setCurrentItem] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const reportingEmployeesState = useSelector(
    (state: any) => state.reportingEmployeesState
  );

  const [dataList, setDataList] = useState<any[]>([]);
  const [originalDataList, setOriginalDataList] = useState<any[]>([]); // Store original unfiltered data
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]); // Default to today's date
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 1,
  });
  const [searchQuery, setSearchQuery] = useState<string>("");
  // Filter state
  const [filterValues, setFilterValues] = useState<AttendanceFilterValues>({
    wing: "",
    department: "",
    office_location_id: "",
    date_from: "",
    date_to: "",
  });

  const fetchRecords = (page: number = 1, limit: number = 10) => {
    dispatch(
      teamDailyAttendance({
        date: DateFormat(date, "YYYY-MM-DD"),
        page: page,
        limit: limit,
      })
    );
  };

  const showLocation = (record: any) => {
    setCurrentItem(record);
    setShowModal(true);
  };

  // Frontend filtering function
  const applyFrontendFilters = (data: any[], filters: AttendanceFilterValues) => {
    let filteredData = [...data];

    // Filter by Wing
    if (filters.wing) {
      filteredData = filteredData.filter((item) => {
        const wing =
          item?.employee?.details?.sap_department_designation_wings?.wing;
        return wing === filters.wing;
      });
    }

    // Filter by Department
    if (filters.department) {
      filteredData = filteredData.filter((item) => {
        const deptId =
          item?.employee?.details?.sap_department_designation_wings
            ?.department_id || item?.employee?.details?.department;
        return deptId === filters.department;
      });
    }

    // Filter by Assigned Location (office_location_id)
    if (filters.office_location_id) {
      filteredData = filteredData.filter((item) => {
        const locId = item?.office_location_id || item?.officeLocation?.id;
        return locId && locId.toString() === filters.office_location_id;
      });
    }

    // Filter by Date Range (From Date)
    if (filters.date_from) {
      const fromDateFilter = new Date(filters.date_from);
      fromDateFilter.setHours(0, 0, 0, 0);
      filteredData = filteredData.filter((item) => {
        const checkInDate = item?.check_in_time
          ? new Date(item.check_in_time)
          : null;
        if (!checkInDate) return false;
        checkInDate.setHours(0, 0, 0, 0);
        return checkInDate >= fromDateFilter;
      });
    }

    // Filter by Date Range (To Date)
    if (filters.date_to) {
      const toDateFilter = new Date(filters.date_to);
      toDateFilter.setHours(23, 59, 59, 999);
      filteredData = filteredData.filter((item) => {
        const checkInDate = item?.check_in_time
          ? new Date(item.check_in_time)
          : null;
        if (!checkInDate) return false;
        return checkInDate <= toDateFilter;
      });
    }

    return filteredData;
  };

  // Handle filter change
  const handleFilterChange = (filters: AttendanceFilterValues) => {
    setFilterValues(filters);
  };

  // Handle search button click - Apply frontend filters
  const handleFilterSearch = () => {
    let filteredData = applyFrontendFilters(originalDataList, filterValues);

    // Apply Emp ID search filter
    if (searchQuery && searchQuery.trim()) {
      filteredData = filteredData.filter((item) => {
        const empId = item?.employee?.empId || item?.empId || "";
        return matchesEmpId(empId, searchQuery);
      });
    }

    setDataList(filteredData);
    setPagination({
      ...pagination,
      current: 1,
      total: filteredData.length,
    });
  };

  // Handle table search (Emp ID search)
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    let filteredData = applyFrontendFilters(originalDataList, filterValues);

    // Apply Emp ID search filter
    if (query && query.trim()) {
      filteredData = filteredData.filter((item) => {
        const empId = item?.employee?.empId || item?.empId || "";
        return matchesEmpId(empId, query);
      });
    }

    setDataList(filteredData);
    setPagination({
      ...pagination,
      current: 1,
      total: filteredData.length,
    });
  };

  // Handle reset
  const handleFilterReset = () => {
    const resetFilters: AttendanceFilterValues = {
      wing: "",
      department: "",
      office_location_id: "",
      date_from: "",
      date_to: "",
    };
    setFilterValues(resetFilters);
    setSearchQuery("");
    setDataList(originalDataList);
    setPagination({
      ...pagination,
      current: 1,
      total: originalDataList.length,
    });
  };

  // Download CSV function
  const handleDownloadCSV = () => {
    try {
      const data = dataList;

      if (data.length === 0) {
        toast.warning("No data available to export");
        return;
      }

      const csvHeaders = [
        "Sr. No.",
        "Emp ID",
        "Employee Name",
        "Wing",
        "Department",
        "Assigned Location",
        "Work Day Type",
        "Is Offline",
        "Check-In",
        "Check-Out",
        "Attendance Status",
        "Verified",
      ];

      const csvRows = data.map((item: any, index: number) => {
        const employee = item.employee;
        const details = employee?.details;
        const deptInfo = details?.sap_department_designation_wings;

        return [
          index + 1,
          employee?.empId || "",
          `${details?.firstname || ""} ${details?.lsastname || ""}`.trim(),
          deptInfo?.wing_name || "",
          deptInfo?.department_name || "",
          item.officeLocation?.address || "",
          item.work_day_type || "",
          item.is_offline || "",
          item.check_in_time ? formatDateTime(item.check_in_time) : "",
          item.check_out_time ? formatDateTime(item.check_out_time) : "",
          item.attendance_status || "",
          item.is_verified || "",
        ];
      });

      const csvContent = [
        csvHeaders.join(","),
        ...csvRows.map((row: any[]) =>
          row
            .map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `daily_attendance_${date}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success("CSV downloaded successfully");
    } catch (error: any) {
      console.error("Error downloading CSV:", error);
      toast.error("Failed to download CSV");
    }
  };

  // useEffect(() => {
  //   fetchRecords();
  // }, []);

  useEffect(() => {
    // Reset to page 1 when date changes
    const resetPage = 1;
    setPagination((prev) => ({ ...prev, current: resetPage }));
    fetchRecords(resetPage, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    if (
      reportingEmployeesState.loading === "fulfilled" &&
      reportingEmployeesState.action === "list"
    ) {
      const result = reportingEmployeesState?.data?.data || null;

      if (result) {
        // Check if result has pagination structure (data array) or is direct array
        const dataArray = result?.data || (Array.isArray(result) ? result : []);
        const total = result?.total || dataArray.length;
        const currentPage = result?.page || 1;
        const pageSize = result?.limit || pagination.pageSize;

        const processedData = Array.isArray(dataArray) ? dataArray : [];
        setOriginalDataList(processedData); // Store original data
        setDataList(processedData);
        setPagination({
          current: currentPage,
          pageSize: pageSize,
          total: total,
        });
        // Reset filters and search when new data is loaded
        setFilterValues({
          wing: "",
          department: "",
          office_location_id: "",
          date_from: "",
          date_to: "",
        });
        setSearchQuery("");
      } else {
        setDataList([]);
        setOriginalDataList([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
        }));
      }
    }
  }, [reportingEmployeesState.loading]);

  // Calculate statistics from dataList
  const statistics = React.useMemo(() => {
    const presence = dataList.filter(
      (item: any) => item.attendance_status === "PRESENT"
    ).length;
    const absence = dataList.filter(
      (item: any) => item.attendance_status === "ABSENT"
    ).length;
    const halfDay = dataList.filter(
      (item: any) => item.attendance_status === "HALF_DAY"
    ).length;
    const lateComing = dataList.filter(
      (item: any) => item.attendance_status === "LATE_COMING"
    ).length;
    const earlyLeaving = dataList.filter(
      (item: any) => item.attendance_status === "EARLY_LEAVING"
    ).length;
    const holiday = dataList.filter(
      (item: any) => item.attendance_status === "HOLIDAY"
    ).length;

    return { presence, absence, halfDay, lateComing, earlyLeaving, holiday };
  }, [dataList]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PRESENT: "#049123",
      ABSENT: "#C0282C",
      HALF_DAY: "#FFA500",
      LATE_COMING: "#B09BFB",
      EARLY_LEAVING: "#FFFF00",
      HOLIDAY: "#969696",
      NOT_CHECKOUT: "#ff9900",
    };
    return colors[status] || "#ccc";
  };

  const columns = [
    {
      title: t("sr_no"),
      render: (_: any, __: any, index: number) =>
        index + 1 + (pagination.current - 1) * pagination.pageSize,
    },
    {
      title: t("emp_id"),
      dataIndex: "employee",
      //sorter: (a: any, b: any) => a.employee.first_name.length - b.employee.first_name.length,
      render: (employee: any) => `${employee?.empId}` || "",
    },
    {
      title: t("employee_name"),
      dataIndex: "employee",
      render: (employee: any, record: any) => {
        const first = employee?.details?.firstname?.trim() || "";
        const last =
          employee?.details?.lsastname?.trim() ||
          employee?.details?.lastname?.trim() ||
          "";

        if (!first && !last) {
          return <div style={{ height: "40px" }}></div>;
        }

        const avatarInfo = randomAvatar(first);

        return (
          <Link
            to={`/employees/profile/${record.employee_id || employee?.employee_id
              }`}
            className="d-flex align-items-center text-decoration-none"
            style={{ color: "inherit" }}
          >
            {employee?.details?.image ? (
              <Avatar
                src={`data:image/jpeg;base64,${employee?.details.image}`}
                className="me-2"
                style={{
                  backgroundColor: avatarInfo.bg,
                  verticalAlign: "middle",
                  width: "50px",
                  height: "50px",
                }}
              ></Avatar>
            ) : (
              <Avatar
                className="me-2"
                style={{
                  backgroundColor: avatarInfo.bg,
                  verticalAlign: "middle",
                  width: "50px",
                  height: "50px",
                }}
              >
                {avatarInfo.initial}
              </Avatar>
            )}
            <div className="text-primary">{`${first} ${last}`.trim()}</div>
          </Link>
        );
      },
    },
    {
      title: t("wing"),
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.wing_name || "-",
    },
    {
      title: t("department"),
      dataIndex: "employee",
      render: (employee: any) =>
        employee?.details?.sap_department_designation_wings?.department_name ||
        "-",
    },
    {
      title: t("assigned_location"),
      dataIndex: "officeLocation",
      render: (officeLocation: any) => officeLocation?.address || "-",
    },
    {
      title: t("work_day_type"),
      dataIndex: "work_day_type",
    },
    {
      title: t("is_offline"),
      dataIndex: "is_offline",
    },
    {
      title: t("check_in_location"),
      dataIndex: "check_in_latitude",
      render: (text: string, record: any) => (
        <div>
          <Link
            to={"#"}
            className="text-decoration-none"
            onClick={() => showLocation(record)}
          >
            <div>
              <b>Latitude</b>: {record.check_in_latitude}
            </div>
            <div>
              <b>Longitude</b>: {record.check_in_longitude}
            </div>
          </Link>
        </div>
      ),
    },
    {
      title: t("check_out_location"),
      dataIndex: "check_out_latitude",
      render: (text: string, record: any) => (
        <div>
          <Link
            to={"#"}
            className="text-decoration-none"
            onClick={() => showLocation(record)}
          >
            <div>
              <b>Latitude</b>: {record.check_out_latitude}
            </div>
            <div>
              <b>Longitude</b>: {record.check_out_longitude}
            </div>
          </Link>
        </div>
      ),
    },
    {
      title: t("check_in"),
      dataIndex: "check_in_time",
      render: (text: string) => (text ? formatDateTime(text) : "N/A"),
    },
    {
      title: t("check_out"),
      dataIndex: "check_out_time",
      render: (text: string) => (text ? formatDateTime(text) : "N/A"),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
    },
    {
      title: t("attendance_status"),
      dataIndex: "attendance_status",
      render: (text: string, record: any) => {
        return (
          <span
            style={{
              backgroundColor: getStatusColor(text),
              color: "#fff",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            {text?.replace(/_/g, " ")?.replace(/\b\w/g, (c) => c.toUpperCase())}
          </span>
        );
      },
    },
    {
      title: t("verified"),
      dataIndex: "is_verified",
    },
  ];
  console.log("record", dataList);
  const handlePagination = (newPagination: any) => {
    const updatedPagination = {
      current: newPagination.current || newPagination.page || 1,
      pageSize: newPagination.pageSize || 10,
      total: newPagination.total || pagination.total,
    };
    setPagination(updatedPagination);
    fetchRecords(updatedPagination.current, updatedPagination.pageSize);
  };

  return (
    <div>
      <Toasts
        show={reportingEmployeesState.loading === "rejected"}
        message={reportingEmployeesState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={
          reportingEmployeesState.loading === "fulfilled" &&
          reportingEmployeesState.action !== "list"
        }
        message={reportingEmployeesState?.data?.message}
        title={"Success"}
        type={"success"}
      />
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">{t("daily_attendance")}</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Attendance
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-2 col-sm-6 mb-3">
              <div
                className="card border-0 shadow-sm"
                style={{ borderRadius: "8px" }}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6
                        className="text-muted mb-2"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Presence
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "24px",
                          fontWeight: "600",
                          color: "#333",
                        }}
                      >
                        {statistics.presence}
                      </h3>
                      <small
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        Days
                      </small>
                    </div>
                    <div
                      className="avatar avatar-md rounded-circle"
                      style={{
                        backgroundColor: "#E8F5E9",
                        width: "50px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="ti ti-user-check fs-20"
                        style={{ color: "#049123" }}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2 col-sm-6 mb-3">
              <div
                className="card border-0 shadow-sm"
                style={{ borderRadius: "8px" }}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6
                        className="text-muted mb-2"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Absence
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "24px",
                          fontWeight: "600",
                          color: "#333",
                        }}
                      >
                        {statistics.absence}
                      </h3>
                      <small
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        Days
                      </small>
                    </div>
                    <div
                      className="avatar avatar-md rounded-circle"
                      style={{
                        backgroundColor: "#FFEBEE",
                        width: "50px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="ti ti-user-x fs-20"
                        style={{ color: "#C0282C" }}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2 col-sm-6 mb-3">
              <div
                className="card border-0 shadow-sm"
                style={{ borderRadius: "8px" }}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6
                        className="text-muted mb-2"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Half Day
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "24px",
                          fontWeight: "600",
                          color: "#333",
                        }}
                      >
                        {statistics.halfDay}
                      </h3>
                      <small
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        Days
                      </small>
                    </div>
                    <div
                      className="avatar avatar-md rounded-circle"
                      style={{
                        backgroundColor: "#F5F5F5",
                        width: "50px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="ti ti-clock-hour-4 fs-20"
                        style={{ color: "#666" }}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2 col-sm-6 mb-3">
              <div
                className="card border-0 shadow-sm"
                style={{ borderRadius: "8px" }}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6
                        className="text-muted mb-2"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Late Coming
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "24px",
                          fontWeight: "600",
                          color: "#333",
                        }}
                      >
                        {statistics.lateComing}
                      </h3>
                      <small
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        Days
                      </small>
                    </div>
                    <div
                      className="avatar avatar-md rounded-circle"
                      style={{
                        backgroundColor: "#F3E5F5",
                        width: "50px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="ti ti-clock-exclamation fs-20"
                        style={{ color: "#B09BFB" }}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2 col-sm-6 mb-3">
              <div
                className="card border-0 shadow-sm"
                style={{ borderRadius: "8px" }}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6
                        className="text-muted mb-2"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Early Leaving
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "24px",
                          fontWeight: "600",
                          color: "#333",
                        }}
                      >
                        {statistics.earlyLeaving}
                      </h3>
                      <small
                        className="text-muted"
                        style={{ fontSize: "11px" }}
                      >
                        Days
                      </small>
                    </div>
                    <div
                      className="avatar avatar-md rounded-circle"
                      style={{
                        backgroundColor: "#FFF9C4",
                        width: "50px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="ti ti-clock-minus fs-20"
                        style={{ color: "#F57C00" }}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-2 col-sm-6 mb-3">
              <div
                className="card border-0 shadow-sm"
                style={{ borderRadius: "8px", backgroundColor: "#9E9E9E" }}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h6
                        className="mb-2"
                        style={{
                          fontSize: "13px",
                          fontWeight: "500",
                          color: "#fff",
                        }}
                      >
                        Holiday
                      </h6>
                      <h3
                        className="mb-0"
                        style={{
                          fontSize: "24px",
                          fontWeight: "600",
                          color: "#fff",
                        }}
                      >
                        {statistics.holiday}
                      </h3>
                      <small
                        style={{
                          fontSize: "11px",
                          color: "rgba(255,255,255,0.8)",
                        }}
                      >
                        Days
                      </small>
                    </div>
                    <div
                      className="avatar avatar-md rounded-circle"
                      style={{
                        backgroundColor: "rgba(255,255,255,0.2)",
                        width: "50px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className="ti ti-calendar-event fs-20"
                        style={{ color: "#fff" }}
                      ></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3">{t("daily_attendance")}</h4>
              <div>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ height: "31px" }}
                  value={date}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    setDate(selectedDate);
                  }}
                />
              </div>
            </div>
            {/* Filter Section */}
            <div className="px-3 pt-3">
              <AttendanceFilter
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                onSearch={handleFilterSearch}
                onReset={handleFilterReset}
                onDownloadCSV={handleDownloadCSV}
                showDownload={true}
                attendanceData={originalDataList}
              />
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={columns}
                dataSource={dataList}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={reportingEmployeesState.loading === "pending"}
                handleSearch={handleSearch}
                searchPlaceholder="Search by Emp ID"
              />
            </div>
          </div>
        </div>
      </div>
      {currentItem && (
        <AttendanceDetailsModel
          show={showModal}
          item={currentItem}
          close={() => {
            setShowModal(false);
            setCurrentItem(null);
          }}
        />
      )}
    </div>
  );
};

export default EmployeesDailyAttendance;
